import { z } from "zod";

export const UserRoleSchema = z.enum(["user", "admin"]);
export type UserRole = z.infer<typeof UserRoleSchema>;

export const ProviderSchema = z.enum([
  "gmail",
  "microsoft",
  "fastmail",
  "yahoo",
  "icloud",
  "custom",
]);
export type Provider = z.infer<typeof ProviderSchema>;

export const ImapConfigSchema = z.object({
  host: z.string().min(1),
  port: z.number().int().positive(),
  secure: z.boolean(),
});
export type ImapConfig = z.infer<typeof ImapConfigSchema>;

export const SmtpConfigSchema = z.object({
  host: z.string().min(1),
  port: z.number().int().positive(),
  secure: z.boolean(),
});
export type SmtpConfig = z.infer<typeof SmtpConfigSchema>;

export const LoginSchema = z.object({
  email: z.string().min(3).regex(/^[^\s@]+@[^\s@]+$/),
  password: z.string().min(1),
});

export const RegisterSchema = z.object({
  email: z.string().min(3).regex(/^[^\s@]+@[^\s@]+$/),
  password: z.string().min(8),
  displayName: z.string().min(1).max(120).optional(),
});

export const AddImapAccountSchema = z.object({
  email: z.string().min(3).regex(/^[^\s@]+@[^\s@]+$/),
  displayName: z.string().min(1).max(120).optional(),
  provider: ProviderSchema.default("custom"),
  password: z.string().min(1),
  imap: ImapConfigSchema,
  smtp: SmtpConfigSchema,
  color: z.string().regex(/^#[0-9a-fA-F]{6}$/).optional(),
});

export const MailAttachmentSchema = z.object({
  filename: z.string().min(1).max(255),
  contentType: z.string().default("application/octet-stream"),
  content: z.string().min(1),
});

export const SendMailSchema = z.object({
  accountId: z.string().uuid(),
  to: z.array(z.string().min(3).regex(/^[^\s@]+@[^\s@]+$/)).min(1),
  cc: z.array(z.string().min(3).regex(/^[^\s@]+@[^\s@]+$/)).optional(),
  bcc: z.array(z.string().min(3).regex(/^[^\s@]+@[^\s@]+$/)).optional(),
  subject: z.string(),
  text: z.string().optional(),
  html: z.string().optional(),
  inReplyTo: z.string().optional(),
  references: z.string().optional(),
  /** Original message being replied to — sets IMAP \\Answered */
  sourceFolder: z.string().min(1).optional(),
  sourceUid: z.number().int().positive().optional(),
  attachments: z.array(MailAttachmentSchema).optional(),
});

export const ListMessagesSchema = z.object({
  accountId: z.string().uuid().optional(),
  folder: z.string().default("INBOX"),
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(100).default(50),
  search: z.string().optional(),
});

export const MessageFlagsSchema = z.object({
  accountId: z.string().uuid(),
  folder: z.string(),
  uid: z.coerce.number().int().positive(),
  seen: z.boolean().optional(),
  flagged: z.boolean().optional(),
  answered: z.boolean().optional(),
  deleted: z.boolean().optional(),
});

export const MoveMessageSchema = z.object({
  accountId: z.string().uuid(),
  folder: z.string(),
  uid: z.coerce.number().int().positive(),
  targetFolder: z.string().min(1),
});

export const UiPreferencesSchema = z.object({
  theme: z.enum(["dawn", "mist", "ink", "midnight"]).default("dawn"),
  density: z.enum(["comfortable", "compact"]).default("comfortable"),
  readingPane: z.enum(["right", "below", "off"]).default("right"),
  defaultAccountId: z.string().uuid().nullable().optional(),
  maxAttachmentSizeMb: z.number().int().min(1).max(100).default(35),
});

export type PublicUser = {
  id: string;
  email: string;
  displayName: string | null;
  role: UserRole;
};

export const UpdateImapAccountSchema = z.object({
  displayName: z.string().min(1).max(120).optional().nullable(),
  color: z.string().regex(/^#[0-9a-fA-F]{6}$/).optional(),
  password: z.string().min(1).optional(),
  imap: ImapConfigSchema.optional(),
  smtp: SmtpConfigSchema.optional(),
});

export const ReorderAccountsSchema = z.object({
  accountIds: z.array(z.string().uuid()).min(1),
});

export type PublicMailAccount = {
  id: string;
  email: string;
  displayName: string | null;
  provider: Provider;
  color: string;
  sortOrder: number;
  imapHost: string;
  smtpHost: string;
  hasOAuth: boolean;
};

export type MailAccountDetail = PublicMailAccount & {
  imapPort: number;
  imapSecure: boolean;
  smtpPort: number;
  smtpSecure: boolean;
};

export type MailFolder = {
  path: string;
  name: string;
  specialUse: string | null;
  delimiter: string;
  listed: boolean;
  subscribed: boolean;
  status?: {
    messages: number;
    unseen: number;
  };
};

export type MailMessageSummary = {
  accountId: string;
  accountEmail: string;
  folder: string;
  uid: number;
  messageId: string | null;
  subject: string;
  from: string;
  to: string;
  date: string | null;
  snippet: string;
  seen: boolean;
  flagged: boolean;
  answered: boolean;
  hasAttachments: boolean;
  size: number;
};

export type MailMessageDetail = MailMessageSummary & {
  cc: string;
  bcc: string;
  html: string | null;
  text: string | null;
  attachments: Array<{
    filename: string;
    contentType: string;
    size: number;
    partId: string;
    /** Content-ID without angle brackets — used for cid: inline images */
    contentId?: string | null;
  }>;
};

export const PROVIDER_PRESETS: Record<
  Exclude<Provider, "custom">,
  { imap: ImapConfig; smtp: SmtpConfig; label: string }
> = {
  gmail: {
    label: "Gmail",
    imap: { host: "imap.gmail.com", port: 993, secure: true },
    smtp: { host: "smtp.gmail.com", port: 465, secure: true },
  },
  microsoft: {
    label: "Microsoft 365 / Outlook",
    imap: { host: "outlook.office365.com", port: 993, secure: true },
    smtp: { host: "smtp.office365.com", port: 587, secure: false },
  },
  fastmail: {
    label: "Fastmail",
    imap: { host: "imap.fastmail.com", port: 993, secure: true },
    smtp: { host: "smtp.fastmail.com", port: 465, secure: true },
  },
  yahoo: {
    label: "Yahoo",
    imap: { host: "imap.mail.yahoo.com", port: 993, secure: true },
    smtp: { host: "smtp.mail.yahoo.com", port: 465, secure: true },
  },
  icloud: {
    label: "iCloud",
    imap: { host: "imap.mail.me.com", port: 993, secure: true },
    smtp: { host: "smtp.mail.me.com", port: 587, secure: false },
  },
};
